/**
 * usePanelCoordination Hook
 *
 * @version 1.0
 * @updated 2026-02-10
 * @module core/layouts/panels
 *
 * @description
 * Hook central para coordinar el estado de visibilidad de los 3 paneles:
 * - LeftPanel (visible por defecto, colapsable manualmente)
 * - RightPanel (oculto por defecto, aparece al seleccionar elemento)
 * - BottomToolbar (siempre visible, ancho dinámico)
 *
 * @remarks
 * **Responsabilidades**:
 * - Gestionar estado de visibilidad de cada panel
 * - Escuchar eventos del mapa (feature.selected, feature.deselected)
 * - Actualizar data-attributes en elemento root para CSS coordinado
 * - Proporcionar callbacks para cambiar estado de paneles
 *
 * @example
 * ```tsx
 * import { usePanelCoordination } from './panels';
 *
 * function MapLayout() {
 *   const {
 *     leftPanelVisible,
 *     toggleLeftPanel,
 *     rightPanelVisible,
 *     selectedFeature,
 *     closeRightPanel,
 *   } = usePanelCoordination();
 *
 *   return (
 *     <div
 *       data-left-panel={leftPanelVisible ? 'visible' : 'collapsed'}
 *       data-right-panel={rightPanelVisible ? 'visible' : 'hidden'}
 *     >
 *       <LeftPanel collapsed={!leftPanelVisible} onToggle={toggleLeftPanel} />
 *       <RightPanel feature={selectedFeature} onClose={closeRightPanel} />
 *       <BottomToolbar />
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { eventBus, CoreEvents } from '../../event-bus';
import type { MapFeatureSelectedPayload } from '../../event-bus/types';
import { useRightPanel } from './RightPanel/RightPanelContext';

/**
 * Estado del sistema de paneles
 */
interface PanelCoordinationState {
  /**
   * Panel izquierdo visible (true por defecto)
   */
  leftPanelVisible: boolean;

  /**
   * Panel derecho visible (false por defecto)
   */
  rightPanelVisible: boolean;

  /**
   * Elemento seleccionado en el mapa (null = sin selección)
   */
  selectedFeature: any | null;

  /**
   * ID de la capa del elemento seleccionado
   */
  selectedLayerId: string | null;
}

/**
 * Acciones del sistema de paneles
 */
interface PanelCoordinationActions {
  /**
   * Alterna el estado de visibilidad del panel izquierdo
   */
  toggleLeftPanel: () => void;

  /**
   * Establece el estado de visibilidad del panel izquierdo
   */
  setLeftPanelVisible: (visible: boolean) => void;

  /**
   * Selecciona un elemento en el mapa (abre RightPanel)
   * Verifica cambios sin guardar antes de cambiar de feature
   * @returns true si se pudo cambiar, false si el usuario canceló
   */
  selectFeature: (feature: any, layerId?: string) => Promise<boolean>;

  /**
   * Cierra el panel derecho (limpia selección)
   */
  closeRightPanel: () => void;

  /**
   * Limpia la selección actual
   */
  clearSelection: () => void;
}

/**
 * Tipo de retorno del hook
 */
type UsePanelCoordinationReturn = PanelCoordinationState & PanelCoordinationActions;

/**
 * Hook usePanelCoordination
 *
 * @remarks
 * Gestiona el estado global de los 3 paneles y coordina su visibilidad.
 * También actualiza los data-attributes en el elemento root para que el CSS pueda reaccionar.
 *
 * @example
 * ```tsx
 * const { leftPanelVisible, rightPanelVisible, toggleLeftPanel, selectFeature } =
 *   usePanelCoordination();
 * ```
 */
export function usePanelCoordination(): UsePanelCoordinationReturn {
  // ===== ESTADO =====

  /**
   * Panel izquierdo: VISIBLE por defecto
   */
  const [leftPanelVisible, setLeftPanelVisible] = useState<boolean>(true);

  /**
   * Panel derecho: OCULTO por defecto (se abre al seleccionar feature)
   */
  const [rightPanelVisible, setRightPanelVisible] = useState<boolean>(false);

  /**
   * Elemento seleccionado: null por defecto
   */
  const [selectedFeature, setSelectedFeature] = useState<any | null>(null);

  /**
   * Capa del elemento seleccionado
   */
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);

  /**
   * Contexto del RightPanel para verificar cambios sin guardar
   */
  const { safeClose } = useRightPanel();

  /**
   * Ref para evitar ciclos infinitos en selectFeature
   */
  const selectingRef = useRef(false);

  // ===== ACCIONES =====

  /**
   * Alterna el estado de visibilidad del panel izquierdo
   */
  const toggleLeftPanel = useCallback((): void => {
    setLeftPanelVisible((prev) => !prev);
  }, []);

  /**
   * Selecciona un elemento en el mapa
   *
   * @param feature - Objeto GeoJSON del elemento seleccionado
   * @param layerId - ID de la capa (opcional)
   * @returns true si se pudo cambiar, false si el usuario canceló
   *
   * @remarks
   * Abre automáticamente el RightPanel.
   * Si hay cambios sin guardar, pide confirmación antes de cambiar.
   */
  const selectFeature = useCallback(async (feature: any, layerId?: string): Promise<boolean> => {
    // Evitar ciclos infinitos
    if (selectingRef.current) {
      console.warn('[usePanelCoordination] ⚠ selectFeature ya en ejecución, ignorando...');
      return false;
    }
    selectingRef.current = true;

    try {
      const featureId = feature?.id || feature?.name || 'unknown';
      console.log(`[usePanelCoordination] → selectFeature(${featureId}, ${layerId})`);

      // Si ya hay un panel abierto y estamos intentando cambiar a otro feature,
      // verificar si hay cambios sin guardar
      if (rightPanelVisible && selectedFeature && feature !== selectedFeature) {
        console.log('[usePanelCoordination] Panel ya abierto, verificando cambios...');
        const canClose = await safeClose();
        if (!canClose) {
          console.warn('[usePanelCoordination] ✗ Usuario canceló el cambio');
          return false; // Usuario canceló, no cambiar
        }
        console.log('[usePanelCoordination] ✓ Cambios verificados, procediendo...');
      }

      console.log(`[usePanelCoordination] ✓ Cambiando a feature: ${featureId}`);
      setSelectedFeature(feature);
      setSelectedLayerId(layerId || null);
      setRightPanelVisible(true);
      return true; // Éxito
    } finally {
      selectingRef.current = false;
    }
  }, [rightPanelVisible, selectedFeature, safeClose]);

  /**
   * Cierra el panel derecho y limpia la selección
   */
  const closeRightPanel = useCallback((): void => {
    console.log('[usePanelCoordination] Cerrando RightPanel');
    setRightPanelVisible(false);
    setSelectedFeature(null);
    setSelectedLayerId(null);
  }, []);

  /**
   * Limpia la selección actual sin cerrar el panel
   */
  const clearSelection = useCallback((): void => {
    console.log('[usePanelCoordination] Limpiando selección');
    setSelectedFeature(null);
    setSelectedLayerId(null);
  }, []);

  // ===== EFECTOS =====

  /**
   * Actualiza data-attributes en el elemento root (#root o body)
   * para que el CSS pueda coordinar el ancho del BottomToolbar
   */
  useEffect(() => {
    const root = document.getElementById('root') || document.body;

    // Actualizar data-attributes
    root.setAttribute('data-left-panel', leftPanelVisible ? 'visible' : 'collapsed');
    root.setAttribute('data-right-panel', rightPanelVisible ? 'visible' : 'hidden');

    console.log('[usePanelCoordination] Data-attributes actualizados:', {
      leftPanel: leftPanelVisible ? 'visible' : 'collapsed',
      rightPanel: rightPanelVisible ? 'visible' : 'hidden',
    });

    return () => {
      // Cleanup: remover data-attributes al desmontar
      root.removeAttribute('data-left-panel');
      root.removeAttribute('data-right-panel');
    };
  }, [leftPanelVisible, rightPanelVisible]);

  /**
   * Escuchar eventos del mapa para abrir/cerrar RightPanel automáticamente
   */
  useEffect(() => {
    // Event listener para feature seleccionado
    const unsubscribeFeatureSelected = eventBus.on<MapFeatureSelectedPayload>(
      CoreEvents.MAP_FEATURE_SELECTED,
      ({ feature, layerId }) => {
        console.log('[usePanelCoordination] Event MAP_FEATURE_SELECTED recibido:', feature, layerId);
        void selectFeature(feature, layerId);
      }
    );

    // Event listener para feature deseleccionado
    const unsubscribeFeatureDeselected = eventBus.on(CoreEvents.MAP_FEATURE_DESELECTED, () => {
      console.log('[usePanelCoordination] Event MAP_FEATURE_DESELECTED recibido');
      closeRightPanel();
    });

    // Cleanup al desmontar
    return () => {
      unsubscribeFeatureSelected();
      unsubscribeFeatureDeselected();
    };
  }, [selectFeature, closeRightPanel]);

  // ===== RETORNO =====

  return {
    // Estado
    leftPanelVisible,
    rightPanelVisible,
    selectedFeature,
    selectedLayerId,

    // Acciones
    toggleLeftPanel,
    setLeftPanelVisible,
    selectFeature,
    closeRightPanel,
    clearSelection,
  };
}

/**
 * Re-export del tipo de retorno para uso en componentes
 */
export type { UsePanelCoordinationReturn };
